import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastService } from './toast.service';

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService implements OnDestroy {
  private readonly ACTIVATED_KEY = 'location_activated';
  private watchId: number | null = null;

  // BehaviorSubject pour émettre les changements de localisation
  private locationSubject = new BehaviorSubject<LocationCoordinates | null>(
    null
  );

  constructor(private toastService: ToastService) {
    // Initialiser avec la localisation si activée
    this.initializeLocation();
  }

  ngOnDestroy(): void {
    this.stopWatchingPosition();
  }

  /**
   * Observable pour s'abonner aux changements de localisation
   */
  get location$(): Observable<LocationCoordinates | null> {
    return this.locationSubject.asObservable();
  }

  /**
   * Récupérer la localisation actuelle
   */
  getCurrentLocation(): LocationCoordinates | null {
    return this.locationSubject.value;
  }

  /**
   * Vérifier si la localisation est activée
   */
  isLocationActivated(): boolean {
    try {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem(this.ACTIVATED_KEY) === 'true';
      }
    } catch (error) {
      console.error(
        'Error reading location activation from localStorage:',
        error
      );
    }
    return false;
  }

  /**
   * Activer la géolocalisation et commencer à suivre la position en temps réel
   * @returns Promise qui se résout avec les coordonnées ou null en cas d'erreur
   */
  activateLocation(): Promise<LocationCoordinates | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        this.toastService.error(
          "La géolocalisation n'est pas supportée par ce navigateur."
        );
        resolve(null);
        return;
      }

      // Arrêter le suivi précédent si actif
      this.stopWatchingPosition();

      // Options pour une meilleure précision
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0, // Ne pas utiliser de position en cache
      };

      // Récupérer la position actuelle immédiatement
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates: LocationCoordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          // Sauvegarder seulement le flag d'activation
          this.saveActivationFlag();

          // Émettre la position actuelle
          this.locationSubject.next(coordinates);

          // Commencer à suivre les changements de position
          this.startWatchingPosition(options);

          resolve(coordinates);
        },
        (error) => {
          this.toastService.error("Impossible d'obtenir la localisation.");
          resolve(null);
        },
        options
      );
    });
  }

  /**
   * Commencer à suivre la position en temps réel
   */
  private startWatchingPosition(options: PositionOptions): void {
    if (!navigator.geolocation) {
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const coordinates: LocationCoordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        // Mettre à jour la position en temps réel
        this.locationSubject.next(coordinates);
      },
      (error) => {
        console.error('Error watching position:', error);
        // En cas d'erreur, arrêter le suivi
        this.stopWatchingPosition();
      },
      options
    );
  }

  /**
   * Arrêter le suivi de la position
   */
  private stopWatchingPosition(): void {
    if (this.watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  /**
   * Charger la localisation actuelle depuis le service
   * Si la localisation est activée, récupère la position actuelle
   */
  loadLocationFromStorage(): LocationCoordinates | null {
    // Retourner la position actuelle depuis le BehaviorSubject
    return this.locationSubject.value;
  }

  /**
   * Récupérer la position actuelle (sans utiliser le cache)
   * @returns Promise qui se résout avec les coordonnées actuelles ou null
   */
  getCurrentPosition(): Promise<LocationCoordinates | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0, // Ne pas utiliser de position en cache
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates: LocationCoordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          resolve(coordinates);
        },
        (error) => {
          console.error('Error getting current position:', error);
          resolve(null);
        },
        options
      );
    });
  }

  /**
   * Sauvegarder seulement le flag d'activation dans localStorage
   */
  private saveActivationFlag(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.ACTIVATED_KEY, 'true');
      }
    } catch (error) {
      console.error(
        'Error saving location activation flag to localStorage:',
        error
      );
    }
  }

  /**
   * Initialiser le service : si la localisation est activée, commencer à suivre
   */
  private initializeLocation(): void {
    if (this.isLocationActivated()) {
      // Si la localisation était activée, recommencer à suivre la position
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      };

      // Récupérer la position actuelle immédiatement
      this.getCurrentPosition().then((coordinates) => {
        if (coordinates) {
          this.locationSubject.next(coordinates);
          // Commencer à suivre les changements
          this.startWatchingPosition(options);
        }
      });
    }
  }

  /**
   * Réinitialiser la localisation (arrêter le suivi et supprimer du localStorage)
   */
  clearLocation(): void {
    // Arrêter le suivi de la position
    this.stopWatchingPosition();

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.ACTIVATED_KEY);
      }
      this.locationSubject.next(null);
    } catch (error) {
      console.error('Error clearing location from localStorage:', error);
    }
  }
}
